import type { IVector3 } from '@seddi/math-v2';
import { type ISeamComponent, type SeamProcess, StitchOrder } from '@seddi/scene';

import { PropsDiff } from '../core/reconciler/propsDiff';
import {
  type SeamRenderable,
  createStitches,
  disposeStitches,
  updateGeometry,
} from '../renderables/seamRenderable';
import { create as createTape, dispose as disposeTape } from '../renderables/seamTapeRenderable';
import { updateColor as updateStitchColor } from '../renderables/stitchRenderable';
import type { SceneReconcilerFrame } from './sceneReconcilerFrame';
import { update as updateTape } from './seamTapeReconciler';

/**
 * Reconciles seam renderable.
 * @param renderable - Renderable.
 * @param prevProps - prev props.
 * @param nextProps - next props,
 * @param frame - reconciler frame.
 */
export const update = (
  renderable: SeamRenderable,
  nextProps: ISeamComponent,
  prevProps: ISeamComponent | undefined,
  frame: SceneReconcilerFrame,
): boolean => {
  const { resources } = frame;

  const diffProps = PropsDiff.diff(nextProps, prevProps);
  let dirty = false;

  // Capture geometry diff before mutations.
  const isGeometryDiffUp = !renderable.garmentGeometry && frame.garmentGeometry;
  const isGeometryDiffDw = renderable.garmentGeometry && !frame.garmentGeometry;
  const isGeometryDirty = frame.entities.get(renderable.id)!.isGeometryDirty;

  // Update geometry.
  if (diffProps.includes('lines') || isGeometryDirty) {
    dirty = updateGeometry(renderable, nextProps, frame.nextScene, frame.garmentGeometry) || dirty;
  }

  // Update stitches.
  if (!frame.config.hideStitches) {
    // Create stitches from garment geometry.
    if (isGeometryDiffUp) {
      const garmentGeometry = frame.garmentGeometry;
      createStitches(renderable, nextProps.process, garmentGeometry, frame.resources);
      dirty = true;
    }
    // Update stitches material.
    if (
      !isGeometryDiffUp &&
      diffProps.includes('process') &&
      prevProps &&
      renderable.stitches.length
    ) {
      const nextProcess = nextProps.process;
      const prevProcess = prevProps.process;
      for (const stitch of renderable.stitches) {
        const colorDiff = stitchColorDiff(nextProcess, prevProcess, stitch.order, stitch.name);
        if (colorDiff) {
          updateStitchColor(stitch, colorDiff, resources);
        }
      }
    }
    // Dispose stitches from garment geometry.
    if (isGeometryDiffDw) {
      disposeStitches(renderable);
      dirty = true;
    }
  }

  // Update tape.
  if (diffProps.includes('tape') || (nextProps.tape && isGeometryDiffUp)) {
    const tape = renderable.tape ?? createTape(renderable.id, renderable.mesh.parent);
    renderable.tape = tape;
    dirty = updateTape(tape, nextProps.tape, prevProps?.tape, frame) || dirty;
  }
  // Dispose tape from garment geometry.
  if (renderable.tape && isGeometryDiffDw) {
    disposeTape(renderable.tape, frame.resources);
    renderable.tape = undefined;
    dirty = true;
  }

  renderable.garmentGeometry = frame.garmentGeometry;
  return dirty;
};

const stitchOrderToProp = (order: StitchOrder): 'stitch' | 'stitchSec' => {
  if (order === StitchOrder.SECONDARY) {
    return 'stitchSec';
  }
  return 'stitch';
};

const stitchColorDiff = (
  nextProps: SeamProcess,
  prevProps: SeamProcess,
  order: StitchOrder,
  name: string,
): IVector3 | undefined => {
  const nextStitch = nextProps[stitchOrderToProp(order)];
  const prevStitch = prevProps[stitchOrderToProp(order)];
  const nextColor = nextStitch[nextStitch.kind][name].color;
  const prevColor = prevStitch[prevStitch.kind][name].color;
  if (nextColor !== prevColor) {
    return nextColor;
  }
  return undefined;
};
